// src/components/sacred/ResponsiveTable.jsx
//
// Sacred-Canvas-aware tabular surface that adapts at 768px:
//   - desktop / tablet (>= 768): MUI Table inside a sticky-headered
//     TableContainer with horizontal scroll. The header row stays
//     pinned while the body scrolls; the surface inherits cream from
//     the page background, so nesting it inside a Card looks correct.
//   - phone (< 768): the same column set is rendered as a vertical
//     Stack of cards. Each card is a label / value pair list using
//     Sacred Canvas tokens (no inline hex, no !important).
//
// API:
//   <ResponsiveTable
//     columns={[{ key, header, render?, mobileLabel?, hideOnMobile? }, …]}
//     rows={items}
//     rowKey={(row) => row.id}
//     cardTitle={(row) => row.date}     // optional — top line on phone
//     emptyContent={…}                  // optional — shown when rows=[]
//     size="small" | "medium"
//     stickyHeader={true|false}         // default true
//     ariaLabel="…"
//   />

import React from 'react';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { radii, space } from '../../theme/sacredCanvas';

const cellSx = (size) => ({
  whiteSpace: 'nowrap',
  ...(size === 'small' ? { py: `${space[2]}px` } : {}),
});

const headerCellSx = {
  fontWeight: 700,
  whiteSpace: 'nowrap',
  backgroundColor: 'var(--sacred-surface-container-low)',
  borderBottom: '1px solid var(--sacred-outline-variant)',
};

const cardSx = {
  p: `${space[5]}px`,
  borderRadius: `${radii.lg}px`,
  backgroundColor: 'var(--sacred-surface-container-low)',
  border: '1px solid var(--sacred-outline-variant)',
};

const ResponsiveTable = ({
  columns,
  rows,
  rowKey,
  cardTitle,
  emptyContent,
  size = 'small',
  stickyHeader = true,
  ariaLabel,
}) => {
  // 768 is the explicit cutoff from uiinnov.md S7 — phone view below.
  const isPhone = useMediaQuery('(max-width: 767.98px)');

  if (!Array.isArray(rows) || rows.length === 0) {
    if (emptyContent) return emptyContent;
    return null;
  }

  const getKey = (row, idx) => {
    if (typeof rowKey === 'function') return rowKey(row);
    if (rowKey && row[rowKey] != null) return row[rowKey];
    return idx;
  };

  if (isPhone) {
    return (
      <Stack
        spacing={`${space[3]}px`}
        role="list"
        aria-label={ariaLabel}
      >
        {rows.map((row, idx) => {
          const title = typeof cardTitle === 'function' ? cardTitle(row) : null;
          return (
            <Box key={getKey(row, idx)} role="listitem" sx={cardSx}>
              {title != null && title !== '' && (
                <Typography
                  variant="subtitle2"
                  component="div"
                  sx={{
                    fontWeight: 700,
                    mb: `${space[2]}px`,
                    color: 'var(--sacred-on-surface)',
                  }}
                >
                  {title}
                </Typography>
              )}
              <Stack spacing={`${space[2]}px`}>
                {columns
                  .filter((c) => !c.hideOnMobile)
                  .map((c) => {
                    const value = c.render ? c.render(row) : row[c.key];
                    if (value == null || value === '' || value === false) {
                      return null;
                    }
                    return (
                      <Box
                        key={c.key}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: `${space[3]}px`,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography
                          variant="caption"
                          component="span"
                          sx={{
                            color: 'var(--sacred-outline)',
                            minWidth: 80,
                            flexShrink: 0,
                          }}
                        >
                          {c.mobileLabel ?? c.header}
                        </Typography>
                        <Box
                          sx={{
                            textAlign: 'end',
                            flex: '1 1 auto',
                            minWidth: 0,
                            wordBreak: 'break-word',
                          }}
                        >
                          {value}
                        </Box>
                      </Box>
                    );
                  })}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    );
  }

  return (
    <TableContainer
      sx={{
        overflowX: 'auto',
        borderRadius: `${radii.md}px`,
      }}
    >
      <Table
        size={size}
        stickyHeader={stickyHeader}
        aria-label={ariaLabel}
      >
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell
                key={c.key}
                align={c.align || 'inherit'}
                sx={headerCellSx}
              >
                {c.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow key={getKey(row, idx)} hover>
              {columns.map((c) => (
                <TableCell
                  key={c.key}
                  align={c.align || 'inherit'}
                  sx={cellSx(size)}
                >
                  {c.render ? c.render(row) : row[c.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ResponsiveTable;
